import React, { useState, useEffect, useCallback } from "react";
import { Flex, Box } from "@mantine/core";
import { GamePadIcon } from "../icons/gamepad";
import { motion } from "framer-motion";
import { GTVMessage, JoinDataType, ThemeType } from "../shared/util/types";
import { DEFAULT_THEME, Games } from "../shared/util/consts";
import DynamicText from "../shared/components/DynamicText";

type LobbyProps = {
  onStartGame: (gameName: Games) => void;
  gameMessage: GTVMessage | null;
  setGameMessage: (msg: GTVMessage | null) => void;
  playMedia: (url: string) => void;
};

const Lobby = ({
  onStartGame,
  gameMessage,
  setGameMessage,
  playMedia,
}: LobbyProps) => {
  const [theme, setTheme] = useState<ThemeType>(DEFAULT_THEME);
  const [players, setPlayers] = useState<string[]>([]);
  const [joinData, setJoinData] = useState<JoinDataType>({
    RoomCode: "Generating...",
    HostName: "gamenighttv.com",
    TotalSessions: 0,
  });
  const [gameName, setGameName] = useState<Games>(Games.Lobby);
  const leftSide = [0, 1, 2, 3, 4];
  const rightSide = [5, 6, 7, 8, 9];
  const updatePlayers = useCallback(
    (newPlayers: string[]) => {
      setPlayers(newPlayers);
      if (newPlayers.length > players.length) {
        playMedia("/drop2.mp3");
      }
    },
    [setPlayers, players, playMedia]
  );
  const handleMessage = useCallback(
    async (msg: GTVMessage) => {
      console.log("Lobby Processing msg", msg);
      if (msg.Theme) {
        setTheme(msg.Theme as ThemeType);
      }
      switch (msg.Type) {
        case "LobbyData":
          setJoinData(msg.Payload.JoinData as JoinDataType);
          updatePlayers(msg.Payload.Players as string[]);
          if (msg.Payload.SelectedGame) {
            setGameName(msg.Payload.SelectedGame as Games);
          } else {
            setGameName(Games.Lobby);
          }
          break;
        case "StartGame":
          await onStartGame(msg.Payload as Games);
          break;
        default:
          console.log("Lobby Unknown message type: " + msg.Type);
          return false;
      }
      return true;
    },
    [onStartGame, updatePlayers]
  );
  useEffect(() => {
    if (gameMessage) {
      setGameMessage(null);
      handleMessage(gameMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMessage]);
  const playerTile = (index: number, leftSide: boolean) => {
    return (
      <motion.div
        key={"player" + index}
        initial={{ x: (leftSide ? "-" : "") + "150%" }}
        animate={{ x: 0 }}
        transition={{ duration: 1, delay: (index % 5) * 0.2 }}
        style={{ height: "16%" }}
      >
        <DynamicText
          style={{
            height: "100%",
            border: "2px solid " + theme.TextColor,
            borderRadius: "30px",
            color: index < players.length ? theme.TextColor : theme.ButtonColor,
            fontSize: 40,
            textAlign: "center",
            marginLeft: '-2px'
          }}
        >
          {index < players.length ? players[index] : "[ OPEN ]"}
        </DynamicText>
      </motion.div>
    );
  };
  const playerColumn = (indices: number[], leftSide: boolean) => {
    return (
      <Flex
        style={{
          width: "30%",
          display: "flex",
          flexDirection: "column",
          padding: "10px",
          justifyContent: "space-between",
          justifyItems: "center",
        }}
      >
        {indices.map((o) => {
          return playerTile(o, leftSide);
        })}
      </Flex>
    );
  };
  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        margin: "25px",
      }}
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1 }}
      >
        <DynamicText
          style={{
            fontSize: 50,
            alignContent: "center",
            textAlign: "center",
            marginTop: 0,
            marginBottom: 0,
          }}
        >
          {gameName}
        </DynamicText>
      </motion.div>
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        {playerColumn(leftSide, true)}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2 }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-around",
              textAlign: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <GamePadIcon size={"30%"} />
            <Box>
              <DynamicText style={{ fontSize: 20 }}>
                {"On your phone browser, go to:"}
              </DynamicText>
              <DynamicText style={{ fontSize: 30, fontWeight: "bold" }}>
                {joinData.HostName}
              </DynamicText>
            </Box>
            <Box style={{}}>
              <DynamicText
                style={{ fontSize: 30, marginTop: 0, marginBottom: 0 }}
              >
                {"Room Code"}
              </DynamicText>
              <DynamicText
                style={{
                  fontSize: 60,
                  marginTop: 0,
                  marginBottom: 0,
                  fontWeight: "bold",
                  color: theme.ReminderColor,
                }}
              >
                {joinData.RoomCode}
              </DynamicText>
            </Box>
          </Box>
        </motion.div>
        {playerColumn(rightSide, false)}
      </Box>
    </Box>
  );
};

export default Lobby;
